// Centralized due date status config
export const DUE_DATE_STATUSES = [
    {
        name: "Past Due",
        alias: "pastDue",
        bgClass: "bg-red-500",
    },
    {
        name: "Next 7 days",
        alias: "next7days",
        bgClass: "bg-yellow-500",
    },
    {
        name: "Thereafter",
        alias: "thereafter",
        bgClass: "bg-green-500",
    },
];

// Helper to get config by alias
export function getDueDateStatusConfig(alias) {
    return DUE_DATE_STATUSES.find((s) => s.alias === alias) || null;
}

// Normalizes date without time
function normalizeDate(date) {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Calculates the difference in days between dueDate and today
function diffInDays(dueDate, today) {
    const startDue = normalizeDate(dueDate);
    const startToday = normalizeDate(today);
    const diffMs = startDue.getTime() - startToday.getTime();
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

function isCompleteStatus(status) {
    const normalizedStatus =
        typeof status === "string" ? status.trim().toLowerCase() : status;

    return (
        normalizedStatus === "complete" ||
        normalizedStatus === "completed" ||
        normalizedStatus === 2
    );
}

/**
 * Since due date returns a date, this function determines its status.:
 * { alias: "pastDue" | "next7days" | "thereafter", name, bgClass }
 */
export function getDueDateStatusFromDate(dueDate, today = new Date()) {
    if (!dueDate) return null;

    const daysDiff = diffInDays(dueDate, today);

    let alias = "thereafter";

    // Centralized range rules:
    // pastDue = before today
    // next7days = today through today + 7 (inclusive)
    // thereafter = after today + 7
    if (daysDiff < 0) {
        alias = "pastDue";
    } else if (daysDiff <= 7) {
        alias = "next7days";
    } else {
        alias = "thereafter";
    }

    const config = getDueDateStatusConfig(alias);

    return {
        ...config,
        daysDiff,
    };
}

export function getDueDateSubLabel(dueDate, status, today = new Date()) {
    if (!dueDate || isCompleteStatus(status)) return null;

    const daysDiff = getDueDateStatusFromDate(dueDate, today)?.daysDiff;

    if (!Number.isFinite(daysDiff)) return null;
    if (daysDiff === 0) return "Due today";

    if (daysDiff > 0) {
        return `${daysDiff} ${daysDiff === 1 ? "day" : "days"} left`;
    }

    const pastDueDays = Math.abs(daysDiff);
    return `${pastDueDays} ${
        pastDueDays === 1 ? "day" : "days"
    } past due`;
}

export function getCompletionSubLabel(dueDate, completionDate, status) {
    if (!dueDate || !completionDate || !isCompleteStatus(status)) return null;

    const daysDiff = diffInDays(completionDate, dueDate);

    if (!Number.isFinite(daysDiff)) return null;
    if (daysDiff === 0) return "Completed on due date";

    if (daysDiff < 0) {
        const daysBefore = Math.abs(daysDiff);
        return `Completed ${daysBefore} ${
            daysBefore === 1 ? "day" : "days"
        } before due date`;
    }

    return `Completed ${daysDiff} ${
        daysDiff === 1 ? "day" : "days"
    } after due date`;
}
